<template>
  <div class="media-wrapper">
    <!-- Image with progressive loading -->
    <v-lazy-image
      v-if="media.mediaType === 'image' && !hasError"
      :src="displaySrc"
      :src-placeholder="thumbnailSrc"
      :alt="media.alt || 'صورة'"
      class="media-content"
      :loading="lazy ? 'lazy' : 'eager'"
      @load="onLoad"
      @error="onError"
    ></v-lazy-image>

    <!-- Video -->
    <video
      v-else-if="media.mediaType === 'video' && !hasError"
      :src="media.url"
      class="media-content"
      muted
      loop
      playsinline
      :poster="thumbnailSrc"
      @loadedmetadata="onLoad"
      @error="onError"
    />

    <!-- Error state -->
    <div v-if="hasError" class="error-state">
      <f7-icon f7="exclamationmark_triangle" size="24"></f7-icon>
      <span>فشل التحميل</span>
    </div>

    <!-- Video indicator -->
    <div v-if="media.mediaType === 'video' && showPlayButton" class="video-indicator">
      <f7-icon f7="play_fill" size="10"></f7-icon>
    </div>

    <!-- Hover overlay -->
    <div v-if="!hasError" class="hover-overlay">
      <f7-icon f7="eye" class="view-icon"></f7-icon>
    </div>
  </div>
</template>

<script setup>
import VLazyImage from 'v-lazy-image';
import { computed, ref } from 'vue';

const props = defineProps({
  media: {
    type: Object,
    required: true,
    validator: (media) =>
      media &&
        typeof media === 'object' &&
        ['image', 'video'].includes(media.mediaType) &&
        typeof media.url === 'string' &&
        media.url.length > 0,
  },
  lazy: {
    type: Boolean,
    default: true,
  },
  showPlayButton: {
    type: Boolean,
    default: false,
  },
  quality: {
    type: String,
    default: 'small',
    validator: (val) => ['thumbnail', 'small', 'medium', 'original'].includes(val),
  },
});

const hasError = ref(false);

const thumbnailSrc = computed(() => {
  if (props.media.mediaType !== 'image') {
    // For videos, return thumbnail from urls or empty string
    return props.media.urls?.thumbnail || props.media.thumbnail || '';
  }

  // For images, return appropriate thumbnail
  return props.media.urls?.thumbnail || props.media.thumbnail || props.media.url;
});

const displaySrc = computed(() => {
  if (props.media.mediaType !== 'image') {
    return props.media.url;
  }

  const { urls } = props.media;
  if (!urls) return props.media.url;

  // Select quality based on prop
  switch (props.quality) {
    case 'thumbnail':
      return urls.thumbnail || urls.small || props.media.url;
    case 'small':
      return urls.small || urls.medium || props.media.url;
    case 'medium':
      return urls.medium || urls.original || props.media.url;
    case 'original':
      return urls.original || props.media.url;
    default:
      return urls.small || props.media.url;
  }
});

const onLoad = () => {
  hasError.value = false;
};

const onError = () => {
  hasError.value = true;

  if (import.meta.env.DEV) {
    console.warn(`Media load failed: ${props.media.mediaType} - ${displaySrc.value}`);
  }
};
</script>

<style scoped>
.media-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background: var(--f7-color-gray-200);
}

.media-content {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.media-content.v-lazy-image {
  filter: blur(10px);
  transform: scale(1.1);
}

.media-content.v-lazy-image-loaded {
  filter: blur(0);
  transform: scale(1);
  transition: filter 0.3s ease, transform 0.3s ease;
}

.error-state {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: var(--f7-color-gray-100);
  color: var(--f7-text-color-secondary);
  font-size: 12px;
}

.video-indicator {
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.75);
  color: white;
  padding: 4px 6px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
  z-index: 2;
  pointer-events: none;
}

.hover-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s ease;
  z-index: 1;
}

.media-wrapper:hover .hover-overlay {
  opacity: 1;
}

.view-icon {
  color: white;
  font-size: 24px;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
}

/* Dark mode support */
.dark .media-wrapper {
  background: #374151;
}

.dark .error-state {
  background: #1f2937;
  color: #9ca3af;
}

/* Mobile responsive */
@media (max-width: 480px) {
  .view-icon {
    font-size: 20px;
  }

  .video-indicator {
    top: 6px;
    right: 6px;
    padding: 3px 5px;
  }
}

/* Accessibility - reduce motion */
@media (prefers-reduced-motion: reduce) {
  .media-content.v-lazy-image-loaded,
  .hover-overlay {
    transition: none;
  }
}
</style>
